import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { frontFilter, paginationConfig } from '@components/gallery/components/gallery-layout-6/config';
import { GalleryLayout6, GalleryLayout6Items } from '@components/gallery/components/gallery-layout-6/model';
import { ContextService } from '@services/context';
import { PageTypeService } from '@services/page-type';
import { ResponsiveService } from '@services/responsive';
import { UrlService } from '@services/url';
import { ImageComponent } from '@shared/components/image';
import { PaginationComponent } from '@shared/components/pagination';
import { VideoComponent } from '@shared/components/video';
import { SplitArrayByPipe } from '@shared/pipes';
import { toBasePageType } from '@shared/utils';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-gallery-layout-6',
  imports: [
    RouterLink,
    RouterLinkActive,
    PaginationComponent,
    SplitArrayByPipe,
    NgTemplateOutlet,
    NgClass,
    ImageComponent,
    VideoComponent,
  ],
  templateUrl: './gallery-layout-6.component.html',
  styleUrl: './gallery-layout-6.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryLayout6Component implements OnInit {
  readonly data = input.required<GalleryLayout6>();
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  totalItems = signal<number>(null!);
  itemsPerPage = signal<number>(null!);
  items = signal<GalleryLayout6Items[]>(null!);
  itemsFiltered = signal<GalleryLayout6Items[]>([]);
  itemsWithoutHighlight = signal<GalleryLayout6Items[]>([]);
  itemsWithoutHighlightReady = signal(false);
  itemsLoaded = signal(false);
  nbPage = signal<number>(null!);
  dataTransformed = signal<GalleryLayout6>(null!);
  categories = signal<Set<string>>(new Set());
  categoryActivated = signal('Tous');
  desktopHeight = signal<number>(null!);
  isTitleDisplayed = signal(false);
  dataLoaded = signal(false);
  showPaginator = signal(true);
  readonly #window = inject(DOCUMENT).defaultView as Window;
  readonly #urlService = inject(UrlService);
  readonly #pageTypeService = inject(PageTypeService);
  readonly #contextService = inject(ContextService);

  constructor() {
    this.#pageTypeService
      .getPageType$()
      .pipe(
        takeUntilDestroyed(),
        tap((pageType) => {
          if (pageType === 'articles') {
            this.isTitleDisplayed.set(true);
            this.itemsPerPage.set(paginationConfig.itemsPerPageArticles);
            this.desktopHeight.set(304);
          } else if (toBasePageType(pageType) === 'projects') {
            this.itemsPerPage.set(paginationConfig.itemsPerPageArticles);
            this.desktopHeight.set(729);
          } else {
            this.itemsPerPage.set(paginationConfig.itemsPerPageDefault);
            this.desktopHeight.set(729);
          }
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.dataTransformed.set({ ...this.data() });
    this.buildGallery();
    this.dataLoaded.set(true);
  }

  onChangePage(page: number): void {
    setTimeout(() => {
      this.itemsWithoutHighlightReady.set(false);
      this.nbPage.set(page);
      this.itemsFiltered.set([
        ...this.items().slice((page - 1) * this.itemsPerPage(), (page - 1) * this.itemsPerPage() + this.itemsPerPage()),
      ]);

      this.itemsWithoutHighlight.set(this.getItemsWithoutHightlightAfterPageChange(this.itemsFiltered()));

      this.itemsWithoutHighlightReady.set(true);
      if (!this.itemsLoaded()) {
        setTimeout(() => {
          this.itemsLoaded.set(true);
        }, 0);
      } else {
        if (this.#contextService.isServer()) return;
        this.#window.scroll({
          top: 0,
          left: 0,
        });
      }
    }, 0);
  }

  filterByCategory(category: string): void {
    this.showPaginator.set(false);
    if (category === this.categoryActivated()) return;
    this.categoryActivated.set(category);
    this.#urlService.setParams({ page: '1' });
    if (category === 'Tous') {
      this.buildGallery(this.data());
    } else {
      const dataItemsFiltered: GalleryLayout6Items[] = this.data().items.filter((item) => item.category === category);
      const dataFiltered = { ...this.data() };
      dataFiltered.items = dataItemsFiltered;
      this.buildGallery(dataFiltered);
    }
    this.showPaginator.set(true);
  }

  private buildGallery(dataFiltered: GalleryLayout6 | null = null): void {
    if (dataFiltered) {
      this.#window.scroll({
        top: 0,
        left: 0,
      });
      this.dataTransformed.set({ ...dataFiltered });
    }
    if (dataFiltered === null && !this.dataTransformed().categories && this.dataTransformed().items) {
      const categories = this.dataTransformed().items.reduce((acc, item) => {
        if (item.category && [...frontFilter].includes(item.category)) {
          acc.push(item.category);
        }
        return acc;
      }, [] as string[]);
      if (categories.length > 0) {
        const sortOrder = ['Tous', ...frontFilter];
        const sortedCategories = Array.from(['Tous', ...categories]).sort((a, b) => {
          const indexA = sortOrder.indexOf(a);
          const indexB = sortOrder.indexOf(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        this.categories.set(new Set(sortedCategories));
      }
    }

    if (
      (!this.dataTransformed().highlight ||
        (this.dataTransformed().highlight && this.dataTransformed().highlight?.length === 0)) &&
      this.dataTransformed().items &&
      this.dataTransformed().items.length > 0
    ) {
      this.dataTransformed.set({
        ...this.dataTransformed(),
        highlight: [this.dataTransformed().items[0]!],
      });
    }
    this.itemsWithoutHighlightReady.set(false);
    this.items.set(this.dataTransformed().items);
    this.itemsFiltered.set(this.getItemsWithoutHightlight(this.dataTransformed()));
    this.itemsWithoutHighlight.set(this.itemsFiltered());
    this.totalItems.set(this.itemsWithoutHighlight().length);
    this.itemsWithoutHighlightReady.set(true);
  }

  private getItemsWithoutHightlight(data: GalleryLayout6): GalleryLayout6Items[] {
    const urlsToExclude = data.highlight?.map((item) => item.url);
    return data.items.filter((item) => !urlsToExclude?.includes(item.url));
  }

  private getItemsWithoutHightlightAfterPageChange(itemsFiltered: GalleryLayout6Items[]): GalleryLayout6Items[] {
    const urlsToExclude = this.dataTransformed().highlight?.map((item) => item.url);
    return itemsFiltered.filter((item) => !urlsToExclude?.includes(item.url));
  }
}
